/**
 * useStoreStatus — global read-only hook exposing whether sales are paused.
 *
 * Polls `/api/store/status` every 30 s so any customer sitting on a page
 * sees the state flip within half a minute of the owner toggling it. The
 * hook is intentionally hookable from any component (no context provider
 * needed) — each mount registers a tiny fetch, and we de-dupe/cache the
 * result in a module-level snapshot so multiple components sharing the
 * same page don't hammer the endpoint.
 *
 * Consumers get: { salesPaused, status, pauseMessage, loading }
 *  - `salesPaused` — true when store_status is "maintenance" or "closed"
 *  - `status`      — raw status ("open" | "high_demand" | "maintenance" | "closed")
 *  - `pauseMessage`— owner-authored copy shown when paused
 *  - `loading`     — true until the first poll returns
 *
 * The server side (`_maintenance_gate`) is the authoritative block; this
 * hook only affects UI so customers never see a checkout choice they
 * can't actually complete.
 */
import { useEffect, useState } from "react";
import api from "./api";

let _snapshot = { salesPaused: false, status: "open", pauseMessage: "", loading: true };
const _listeners = new Set();
let _timerStarted = false;

const _emit = (next) => {
  _snapshot = { ..._snapshot, ...next };
  _listeners.forEach(fn => fn(_snapshot));
};

const _poll = async () => {
  try {
    const { data } = await api.get("/store/status");
    _emit({
      salesPaused: !!data.sales_paused,
      status: data.status || "open",
      pauseMessage: data.pause_message || data.message || "",
      loading: false,
    });
  } catch {
    _emit({ loading: false });
  }
};

const _startTimer = () => {
  if (_timerStarted) return;
  _timerStarted = true;
  _poll();
  setInterval(_poll, 30_000);
};

export function useStoreStatus() {
  const [s, setS] = useState(_snapshot);
  useEffect(() => {
    _startTimer();
    _listeners.add(setS);
    setS(_snapshot);
    return () => { _listeners.delete(setS); };
  }, []);
  return s;
}

// One-shot refresh — call after an admin toggles the pause state so the UI
// updates immediately instead of waiting for the next poll tick.
export const refreshStoreStatus = _poll;
